import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth, firestore } from '../../firebase/config';
import { 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  updateDoc,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import LocationPicker from './LocationPicker';

// JobModal component for editing job
const JobModal = ({ 
  show, 
  onClose, 
  jobForm,
  setJobForm,
  skillInput,
  setSkillInput,
  benefitInput,
  setBenefitInput,
  useBusinessLocation,
  setUseBusinessLocation,
  customLocation,
  setCustomLocation,
  businessProfile,
  memoizedBusinessAddress,
  submitting,
  onSubmit,
  onAddSkill,
  onRemoveSkill,
  onAddBenefit,
  onRemoveBenefit
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-lg w-full max-h-[95vh] overflow-y-auto ${
        !useBusinessLocation ? 'max-w-4xl' : 'max-w-2xl'
      }`}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium text-gray-900">Edit Job Post</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            {/* Job Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Title *
              </label>
              <input
                type="text"
                required
                value={jobForm.title}
                onChange={(e) => setJobForm({...jobForm, title: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                placeholder="e.g., Server, Chef, Manager"
              />
            </div>

            {/* Job Type and Experience Level */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Type *
                </label>
                <select
                  required
                  value={jobForm.jobType}
                  onChange={(e) => setJobForm({...jobForm, jobType: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">Select Type</option>
                  <option value="Full-time">Full-time</option>
                  <option value="Part-time">Part-time</option>
                  <option value="Contract">Contract</option>
                  <option value="Temporary">Temporary</option>
                  <option value="Seasonal">Seasonal</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Experience Level *
                </label>
                <select
                  required
                  value={jobForm.experienceLevel}
                  onChange={(e) => setJobForm({...jobForm, experienceLevel: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">Select Level</option>
                  <option value="Entry Level">Entry Level</option>
                  <option value="1-2 years">1-2 years</option>
                  <option value="3-5 years">3-5 years</option>
                  <option value="5+ years">5+ years</option>
                  <option value="Manager">Manager</option>
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Job Description *
              </label>
              <textarea
                required
                rows={4}
                value={jobForm.description}
                onChange={(e) => setJobForm({...jobForm, description: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                placeholder="Describe the role, responsibilities, and what you're looking for..."
              />
            </div>

            {/* Work Schedule */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Work Schedule
              </label>
              <input
                type="text"
                value={jobForm.workSchedule}
                onChange={(e) => setJobForm({...jobForm, workSchedule: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                placeholder="e.g., Monday-Friday 9am-5pm, Weekends, Flexible hours"
              />
            </div>

            {/* Start Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Time
              </label>
              <input
                type="text"
                value={jobForm.startTime}
                onChange={(e) => setJobForm({...jobForm, startTime: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                placeholder="e.g., Immediately, Next Monday, 2 weeks notice"
              />
            </div>

            {/* Pay Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hourly Rate ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={jobForm.hourlyRate}
                  onChange={(e) => setJobForm({...jobForm, hourlyRate: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  placeholder="e.g., 15.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Salary (if different)
                </label>
                <input
                  type="text"
                  value={jobForm.salary}
                  onChange={(e) => setJobForm({...jobForm, salary: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  placeholder="e.g., $35,000/year or $15-18/hr"
                />
              </div>
            </div>

            {/* Required Skills */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Required Skills
              </label>
              <div className="flex space-x-2 mb-2">
                <input
                  type="text"
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), onAddSkill())}
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter a skill and press Enter"
                />
                <button
                  type="button"
                  onClick={onAddSkill}
                  className="px-3 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {jobForm.requiredSkills.map((skill, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800"
                  >
                    {skill}
                    <button
                      type="button"
                      onClick={() => onRemoveSkill(skill)}
                      className="ml-1 text-orange-600 hover:text-orange-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Extra Compensations */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Extra Compensations
              </label>
              <div className="flex space-x-2 mb-2">
                <input
                  type="text"
                  value={benefitInput}
                  onChange={(e) => setBenefitInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), onAddBenefit())}
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter a benefit and press Enter"
                />
                <button
                  type="button"
                  onClick={onAddBenefit}
                  className="px-3 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {jobForm.extraCompensations.map((benefit, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800"
                  >
                    {benefit}
                    <button
                      type="button"
                      onClick={() => onRemoveBenefit(benefit)}
                      className="ml-1 text-orange-600 hover:text-orange-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Job Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Job Location *
              </label>
              
              {/* Location Options */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="business-location"
                    name="location-type"
                    checked={useBusinessLocation}
                    onChange={() => {
                      setUseBusinessLocation(true);
                      setCustomLocation(null);
                    }}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                  />
                  <label htmlFor="business-location" className="ml-2 block text-sm text-gray-900">
                    Use Business Location
                    {businessProfile?.address && (
                      <span className="text-gray-500 ml-1">
                        ({businessProfile.address.city}, {businessProfile.address.state})
                      </span>
                    )}
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="custom-location"
                    name="location-type"
                    checked={!useBusinessLocation}
                    onChange={() => setUseBusinessLocation(false)}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300"
                  />
                  <label htmlFor="custom-location" className="ml-2 block text-sm text-gray-900">
                    Select Different Location
                  </label>
                </div>
              </div>

              {/* Custom Location Picker */}
              {!useBusinessLocation && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <LocationPicker
                    onLocationSelect={(location) => setCustomLocation(location)}
                    initialLocation={customLocation?.coordinates ? {
                      lat: customLocation.coordinates.latitude,
                      lng: customLocation.coordinates.longitude
                    } : null}
                    businessAddress={memoizedBusinessAddress}
                  />
                </div>
              )}

              {/* Current Location Display */}
              {useBusinessLocation && businessProfile?.address && (
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-900 mb-1">Business Location:</p>
                  <p className="text-sm text-gray-600">
                    {businessProfile.address.street && `${businessProfile.address.street}, `}
                    {businessProfile.address.city}, {businessProfile.address.state} {businessProfile.address.zipcode}
                  </p>
                </div>
              )}

              {!useBusinessLocation && customLocation && (
                <div className="bg-slate-50 rounded-lg p-3 mt-2">
                  <p className="text-sm font-medium text-gray-900 mb-1">Selected Location:</p>
                  <p className="text-sm text-gray-600">
                    {customLocation.street && `${customLocation.street}, `}
                    {customLocation.city}, {customLocation.state} {customLocation.zipcode}
                  </p>
                  {customLocation.coordinates && (
                    <p className="text-xs text-gray-500 mt-1">
                      Coordinates: {customLocation.coordinates.latitude.toFixed(6)}, {customLocation.coordinates.longitude.toFixed(6)}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Application URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Application URL (Optional)
              </label>
              <input
                type="url"
                value={jobForm.applicationUrl}
                onChange={(e) => setJobForm({...jobForm, applicationUrl: e.target.value})}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                placeholder="https://example.com/apply"
              />
            </div>

            {/* Immediate Start */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="immediateStart"
                checked={jobForm.isImmediateStart}
                onChange={(e) => setJobForm({...jobForm, isImmediateStart: e.target.checked})}
                className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
              />
              <label htmlFor="immediateStart" className="ml-2 block text-sm text-gray-900">
                Immediate start available
              </label>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50"
              >
                {submitting ? 'Saving...' : 'Update Job'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const BusinessJobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applicationsLoading, setApplicationsLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('overview');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Edit job modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Job form state
  const [jobForm, setJobForm] = useState({
    title: '',
    description: '',
    jobType: '',
    experienceLevel: '',
    hourlyRate: '',
    salary: '',
    workSchedule: '',
    requiredSkills: [],
    extraCompensations: [],
    startTime: '',
    isImmediateStart: false,
    applicationUrl: ''
  });

  // Edit form input states
  const [skillInput, setSkillInput] = useState('');
  const [benefitInput, setBenefitInput] = useState('');
  const [useBusinessLocation, setUseBusinessLocation] = useState(true);
  const [customLocation, setCustomLocation] = useState(null);

  // Memoize business address
  const memoizedBusinessAddress = useMemo(() => {
    return businessProfile?.address || null;
  }, [businessProfile?.address]);

  useEffect(() => {
    if (jobId) {
      fetchJobDetails();
      fetchApplications();
      fetchBusinessProfile();
    }
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      const jobDoc = await getDoc(doc(firestore, 'jobs', jobId));
      if (jobDoc.exists()) {
        setJob({ id: jobDoc.id, ...jobDoc.data() });
      } else {
        navigate('/business-dashboard/jobs');
      }
    } catch (error) {
      console.error('Error fetching job details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      console.log(`🔍 BusinessJobDetail: Fetching applications for job ${jobId}`);
      
      // Get current user's business ID first
      const user = auth.currentUser;
      if (!user) {
        console.error('❌ No authenticated user');
        setApplicationsLoading(false);
        return;
      }

      const userDoc = await getDoc(doc(firestore, 'users', user.uid));
      if (!userDoc.exists() || !userDoc.data().businessId) {
        console.error('❌ No business ID found for user');
        setApplicationsLoading(false);
        return;
      }

      const businessId = userDoc.data().businessId;
      console.log(`🏢 Using business ID: ${businessId}`);
      
      // Use the same strategy as BusinessApplicants: fetch all applications and filter client-side
      console.log('📝 Fetching all applications (filtering client-side)...');
      const allApplicationsSnapshot = await getDocs(collection(firestore, 'jobApplications'));
      const allApplications = allApplicationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log(`📊 Total applications in database: ${allApplications.length}`);
      
      // Debug: Show some sample applications to understand the data structure
      console.log(`🔍 Sample applications in database:`, allApplications.slice(0, 5));
      
      // Check if any applications have the jobId we're looking for
      const applicationsWithJobId = allApplications.filter(app => app.jobId === jobId);
      console.log(`🎯 Applications with jobId ${jobId}:`, applicationsWithJobId);
      
      // Check if any applications have the businessId we're looking for
      const applicationsWithBusinessId = allApplications.filter(app => app.businessId === businessId);
      console.log(`🏢 Applications with businessId ${businessId}:`, applicationsWithBusinessId);
      
      // Filter applications for this specific job
      const jobApplications = allApplications.filter(app => {
        const matchesJob = app.jobId === jobId;
        const matchesBusiness = app.businessId === businessId;
        
        if (matchesJob) {
          console.log(`✅ Found application for job ${jobId}:`, app);
          console.log(`🔍 Application businessId: "${app.businessId}" (type: ${typeof app.businessId})`);
          console.log(`🔍 Expected businessId: "${businessId}" (type: ${typeof businessId})`);
          console.log(`🔍 BusinessId match: ${app.businessId === businessId}`);
          console.log(`🔍 BusinessId match (loose): ${app.businessId == businessId}`);
        }
        
        // Temporary: Allow applications with just jobId match while we fix businessId data
        // TODO: Change back to (matchesJob && matchesBusiness) after running fix script
        return matchesJob;
      });
      
      console.log(`📋 Found ${jobApplications.length} applications for job ${jobId}`);
      
      // If no applications found with both criteria, let's try with just jobId for debugging
      if (jobApplications.length === 0) {
        console.warn(`⚠️ No applications found with both jobId AND businessId criteria`);
        console.warn(`⚠️ Falling back to show applications with just jobId match for debugging`);
        
        const jobOnlyApplications = allApplications.filter(app => app.jobId === jobId);
        console.log(`📋 Applications with just jobId match: ${jobOnlyApplications.length}`);
        
        if (jobOnlyApplications.length > 0) {
          // Use these applications but add a warning
          console.warn(`⚠️ Using applications that match jobId but may have different businessId`);
          jobApplications.push(...jobOnlyApplications);
        }
      }
      
      // Sort by application date
      jobApplications.sort((a, b) => {
        const dateA = a.appliedAt?.toDate?.() || new Date(a.appliedAt) || a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
        const dateB = b.appliedAt?.toDate?.() || new Date(b.appliedAt) || b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
        return dateB - dateA; // descending order (newest first)
      });

      // Fetch user profiles for applicants
      const applicationsWithProfiles = await Promise.all(
        jobApplications.map(async (applicationData) => {
          // Fetch user profile data
          try {
            const userProfileDoc = await getDoc(doc(firestore, 'userProfiles', applicationData.userId));
            if (userProfileDoc.exists()) {
              applicationData.userProfile = userProfileDoc.data();
              console.log(`👤 Loaded profile for user ${applicationData.userId}`);
            } else {
              // Fallback to users collection
              const userDoc = await getDoc(doc(firestore, 'users', applicationData.userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                applicationData.userProfile = {
                  firstName: userData.firstName || '',
                  lastName: userData.lastName || '',
                  email: userData.email || '',
                  phone: userData.phone || ''
                };
                console.log(`👤 Loaded fallback profile for user ${applicationData.userId}`);
              }
            }
          } catch (error) {
            console.error(`❌ Error fetching user profile for ${applicationData.userId}:`, error);
          }

          return applicationData;
        })
      );

      setApplications(applicationsWithProfiles);
      console.log(`✅ Successfully loaded ${applicationsWithProfiles.length} applications with profiles`);
      
    } catch (error) {
      console.error('❌ Error fetching applications:', error);
    } finally {
      setApplicationsLoading(false);
    }
  };

  const fetchBusinessProfile = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const userDoc = await getDoc(doc(firestore, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.businessId) {
          const businessDoc = await getDoc(doc(firestore, 'businesses', userData.businessId));
          if (businessDoc.exists()) {
            setBusinessProfile(businessDoc.data());
          }
        }
      }
    } catch (error) {
      console.error('Error fetching business profile:', error);
    }
  };

  const handleStatusChange = async (applicationId, newStatus) => {
    try {
      await updateDoc(doc(firestore, 'jobApplications', applicationId), {
        status: newStatus,
        updatedAt: new Date()
      });
      
      // Update local state
      setApplications(applications.map(app => 
        app.id === applicationId 
          ? { ...app, status: newStatus }
          : app
      ));
    } catch (error) {
      console.error('Error updating application status:', error);
    }
  };

  const handleToggleJobStatus = async () => {
    const newStatus = job.isActive !== false ? false : true;
    const actionText = newStatus ? 'activate' : 'deactivate';
    
    if (!window.confirm(`Are you sure you want to ${actionText} this job posting?`)) {
      return;
    }

    try {
      await updateDoc(doc(firestore, 'jobs', jobId), {
        isActive: newStatus,
        updatedAt: new Date()
      });
      
      // Update local state
      setJob({ ...job, isActive: newStatus });
      
              // alert(`Job ${newStatus ? 'activated' : 'deactivated'} successfully!`); // Disabled notification
    } catch (error) {
      console.error('Error updating job status:', error);
      alert('Error updating job status. Please try again.');
    }
  };

  const handleEditJob = () => {
    if (!job) return;
    
    setJobForm({
      title: job.title || '',
      description: job.description || '',
      jobType: job.jobType || '',
      experienceLevel: job.experienceLevel || '',
      hourlyRate: job.hourlyRate || '',
      salary: job.salary || '',
      workSchedule: job.workSchedule || '',
      requiredSkills: job.requiredSkills || [],
      extraCompensations: job.extraCompensations || [],
      startTime: job.startTime || '',
      isImmediateStart: job.isImmediateStart || false,
      applicationUrl: job.applicationUrl || ''
    });

    // Set location state
    if (job.address && businessProfile?.address) {
      const jobAddr = job.address;
      const bizAddr = businessProfile.address;
      
      const isSameAddress = (
        jobAddr.city === bizAddr.city &&
        jobAddr.state === bizAddr.state &&
        jobAddr.street === bizAddr.street &&
        jobAddr.zipcode === bizAddr.zipcode
      );
      
      setUseBusinessLocation(isSameAddress);
      if (!isSameAddress) {
        setCustomLocation(job.address);
      }
    }

    setShowEditModal(true);
  };

  const handleSubmitJob = useCallback(async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const user = auth.currentUser;
      if (!user) return;

      // Validate location selection
      if (!useBusinessLocation && !customLocation) {
        alert('Please select a job location before submitting.');
        setSubmitting(false);
        return;
      }

      if (useBusinessLocation && (!businessProfile?.address || !businessProfile.address.city)) {
        alert('Business location is incomplete. Please update your business profile or select a custom location.');
        setSubmitting(false);
        return;
      }

      // Determine job location
      let jobAddress;
      if (useBusinessLocation && businessProfile?.address) {
        jobAddress = {
          ...businessProfile.address,
          coordinates: businessProfile.address.coordinates || {
            latitude: 0,
            longitude: 0
          }
        };
      } else if (customLocation) {
        jobAddress = customLocation;
      } else {
        jobAddress = {
          city: businessProfile?.address?.city || '',
          state: businessProfile?.address?.state || '',
          street: businessProfile?.address?.street || '',
          zipcode: businessProfile?.address?.zipcode || '',
          coordinates: {
            latitude: 0,
            longitude: 0
          }
        };
      }

      const jobData = {
        ...jobForm,
        address: jobAddress,
        updatedAt: serverTimestamp()
      };

      await updateDoc(doc(firestore, 'jobs', jobId), jobData);
      
      // Update local state
      setJob({ ...job, ...jobData });
      setShowEditModal(false);
              // alert('Job updated successfully!'); // Disabled notification
      
    } catch (error) {
      console.error('Error updating job:', error);
      alert('Error updating job. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [jobForm, useBusinessLocation, customLocation, businessProfile, job, jobId]);

  // Helper functions for managing arrays
  const addSkill = useCallback(() => {
    if (skillInput.trim() && !jobForm.requiredSkills.includes(skillInput.trim())) {
      setJobForm({
        ...jobForm,
        requiredSkills: [...jobForm.requiredSkills, skillInput.trim()]
      });
      setSkillInput('');
    }
  }, [skillInput, jobForm]);

  const removeSkill = useCallback((skillToRemove) => {
    setJobForm({
      ...jobForm,
      requiredSkills: jobForm.requiredSkills.filter(skill => skill !== skillToRemove)
    });
  }, [jobForm]);

  const addBenefit = useCallback(() => {
    if (benefitInput.trim() && !jobForm.extraCompensations.includes(benefitInput.trim())) {
      setJobForm({
        ...jobForm,
        extraCompensations: [...jobForm.extraCompensations, benefitInput.trim()]
      });
      setBenefitInput('');
    }
  }, [benefitInput, jobForm]);

  const removeBenefit = useCallback((benefitToRemove) => {
    setJobForm({
      ...jobForm,
      extraCompensations: jobForm.extraCompensations.filter(benefit => benefit !== benefitToRemove)
    });
  }, [jobForm]);

  const formatJobLocation = (address) => {
    if (!address) return 'Location not specified';
    const parts = [];
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    return parts.length > 0 ? parts.join(', ') : 'Location not specified';
  };

  const getPostedDays = (createdAt) => {
    if (!createdAt) return 0;
    const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phone;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-gradient-to-r from-yellow-50 to-yellow-100 text-yellow-700 border border-yellow-200/50';
      case 'reviewed':
        return 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 border border-blue-200/50';
      case 'accepted':
        return 'bg-gradient-to-r from-green-50 to-green-100 text-green-700 border border-green-200/50';
      case 'rejected':
        return 'bg-gradient-to-r from-red-50 to-red-100 text-red-700 border border-red-200/50';
      default:
        return 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 border border-gray-200/50';
    }
  };

  const filteredApplications = applications.filter(app => {
    if (filterStatus === 'all') return true;
    return app.status === filterStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF8C00]"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Job not found</h2>
        <button
          onClick={() => navigate('/business-dashboard/jobs')}
          className="mt-4 px-4 py-2 bg-[#FF8C00] text-white rounded-lg hover:bg-[#FF8C00]/90"
        >
          Back to Jobs
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/business-dashboard/jobs')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Jobs
        </button>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{job.title}</h1>
              <div className="flex items-center space-x-4 text-gray-600 mb-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  {formatJobLocation(job.address)}
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  Posted {getPostedDays(job.createdAt)} days ago
                </div>
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                  {applications.length} applications
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`px-3 py-1 text-sm font-semibold rounded-full ${
                  job.isActive !== false
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {job.isActive !== false ? 'Active' : 'Inactive'}
                </span>
                <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                  {job.jobType}
                </span>
                {(job.hourlyRate || job.salary) && (
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full font-medium">
                    {job.hourlyRate ? `$${job.hourlyRate}/hr` : job.salary}
                  </span>
                )}
              </div>
            </div>
            <div className="flex space-x-3">
              <button 
                onClick={handleEditJob}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-slate-50"
              >
                Edit Job
              </button>
              <button 
                onClick={handleToggleJobStatus}
                className={`px-4 py-2 rounded-lg font-medium ${
                  job.isActive !== false
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'bg-green-500 text-white hover:bg-green-600'
                }`}
              >
                {job.isActive !== false ? 'Deactivate Job' : 'Activate Job'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'applications', label: `Applications (${applications.length})` }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setSelectedTab(tab.key)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                selectedTab === tab.key
                  ? 'border-[#FF8C00] text-[#FF8C00]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      {selectedTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Job Details */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Job Description</h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{job.description}</p>
            </div>

            {job.requiredSkills && job.requiredSkills.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Required Skills</h2>
                <div className="flex flex-wrap gap-2">
                  {job.requiredSkills.map((skill, index) => (
                    <span key={index} className="px-3 py-1 bg-[#FF8C00]/10 text-[#FF8C00] text-sm rounded-lg font-medium">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {job.extraCompensations && job.extraCompensations.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Benefits & Perks</h2>
                <div className="space-y-2">
                  {job.extraCompensations.map((benefit, index) => (
                    <div key={index} className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Job Information</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-600">Experience Level</span>
                  <p className="font-medium text-gray-900">{job.experienceLevel || 'Not specified'}</p>
                </div>
                {job.workSchedule && (
                  <div>
                    <span className="text-sm text-gray-600">Work Schedule</span>
                    <p className="font-medium text-gray-900">{job.workSchedule}</p>
                  </div>
                )}
                {job.startTime && (
                  <div>
                    <span className="text-sm text-gray-600">Start Time</span>
                    <p className="font-medium text-gray-900">{job.startTime}</p>
                  </div>
                )}
                {job.isImmediateStart && (
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-medium text-green-700">Immediate start available</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Total Applications</span>
                  <span className="font-medium text-gray-900">{applications.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Pending Review</span>
                  <span className="font-medium text-yellow-600">{applications.filter(app => app.status === 'pending').length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Under Review</span>
                  <span className="font-medium text-blue-600">{applications.filter(app => app.status === 'reviewed').length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Hired</span>
                  <span className="font-medium text-green-600">{applications.filter(app => app.status === 'hired').length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedTab === 'applications' && (
        <div>
          {/* Application Filters */}
          <div className="mb-8 bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-[#FF8C00]/10 rounded-xl flex items-center justify-center mr-3">
                  <svg className="w-4 h-4 text-[#FF8C00]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-sm font-semibold text-gray-900">Filter Applications</span>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'all', label: 'All', icon: '📋', color: 'gray' },
                  { key: 'pending', label: 'Pending', icon: '⏳', color: 'yellow' },
                  { key: 'reviewed', label: 'Reviewing', icon: '👀', color: 'blue' },
                  { key: 'hired', label: 'Hired', icon: '✅', color: 'green' },
                  { key: 'rejected', label: 'Rejected', icon: '❌', color: 'red' }
                ].map((filter) => {
                  const count = filter.key === 'all' ? applications.length : applications.filter(app => app.status === filter.key).length;
                  const isActive = filterStatus === filter.key;
                  
                  return (
                    <button
                      key={filter.key}
                      onClick={() => setFilterStatus(filter.key)}
                      className={`inline-flex items-center px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                        isActive
                          ? 'bg-[#FF8C00] text-white shadow-md transform scale-105'
                          : 'bg-slate-50 text-gray-700 hover:bg-gray-100 hover:scale-102'
                      }`}
                    >
                      <span className="mr-2">{filter.icon}</span>
                      {filter.label}
                      <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                        isActive 
                          ? 'bg-white/20 text-white' 
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Summary Stats */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>
                  Showing {filteredApplications.length} of {applications.length} applications
                </span>
                <div className="flex items-center space-x-4">
                  <span className="flex items-center">
                                      <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                  {Math.round((applications.filter(app => app.status === 'hired').length / applications.length) * 100) || 0}% hire rate
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Applications List */}
          {applicationsLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF8C00]"></div>
            </div>
          ) : filteredApplications.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No applications yet</h3>
              <p className="text-gray-600">Applications will appear here once candidates apply to this job.</p>
            </div>
          ) : (
            <div className="bg-white shadow-lg rounded-2xl overflow-hidden border border-gray-200">
              <ul className="divide-y divide-gray-100">
                {filteredApplications.map((application) => {
                  return (
                    <li key={application.id} className="hover:bg-gradient-to-r hover:from-[#FF8C00]/5 hover:to-transparent transition-all duration-200">
                                             <div className="px-6 py-5 sm:px-8">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-6 flex-1">
                            {/* Enhanced Profile Picture */}
                            <div className="relative flex-shrink-0">
                              {application.userProfile?.profileImageUrl ? (
                                <div className="relative">
                                  <img 
                                    src={application.userProfile.profileImageUrl} 
                                    alt="Profile" 
                                    className="w-16 h-16 rounded-2xl object-cover border-3 border-white shadow-lg"
                                  />
                                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 border-2 border-white rounded-full"></div>
                                </div>
                              ) : (
                                <div className="relative">
                                  <div className="w-16 h-16 bg-gradient-to-br from-[#FF8C00] via-[#FF7A00] to-[#FF6B35] rounded-2xl flex items-center justify-center shadow-lg border-3 border-white">
                                    <span className="text-white font-bold text-xl drop-shadow-sm">
                                      {application.userProfile?.firstName?.charAt(0) || application.applicantName?.charAt(0) || '?'}
                                    </span>
                                  </div>
                                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 border-2 border-white rounded-full"></div>
                                </div>
                              )}
                            </div>

                            {/* Applicant Information */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <h3 className="text-xl font-bold text-gray-900 truncate hover:text-[#FF8C00] transition-colors">
                                    {application.userProfile?.firstName && application.userProfile?.lastName
                                      ? `${application.userProfile.firstName} ${application.userProfile.lastName}`
                                      : application.applicantName || 'Anonymous'}
                                  </h3>
                                  <p className="text-gray-600 text-sm mt-1">
                                    {application.userProfile?.email || application.applicantEmail || 'No email provided'}
                                  </p>
                                </div>
                              </div>

                              {/* Application Info */}
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                <div className="flex items-center text-sm text-gray-600">
                                  <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center mr-3">
                                    <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                  <div>
                                    <p className="font-medium">Applied</p>
                                    <p className="text-xs text-gray-500">{getPostedDays(application.appliedAt)} days ago</p>
                                  </div>
                                </div>

                                {application.userProfile?.phone && (
                                  <div className="flex items-center text-sm text-gray-600">
                                    <div className="w-8 h-8 bg-green-50 rounded-xl flex items-center justify-center mr-3">
                                      <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                      </svg>
                                    </div>
                                    <div>
                                      <p className="font-medium">Phone</p>
                                      <p className="text-xs text-gray-500">{formatPhoneNumber(application.userProfile.phone)}</p>
                                    </div>
                                  </div>
                                )}

                                <div className="flex items-center text-sm text-gray-600">
                                  <div className="w-8 h-8 bg-purple-50 rounded-xl flex items-center justify-center mr-3">
                                    <svg className="w-4 h-4 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                  <div>
                                    <p className="font-medium">Status</p>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full ${getStatusColor(application.status)}`}>
                                      <div className="w-1.5 h-1.5 rounded-full mr-1.5 bg-current opacity-75"></div>
                                      {application.status === 'pending' && 'Pending Review'}
                                      {application.status === 'reviewed' && 'Under Review'}
                                      {application.status === 'hired' && 'Hired'}
                                      {application.status === 'rejected' && 'Rejected'}
                                      {!application.status && 'Pending Review'}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Cover Letter Preview */}
                              {application.coverLetter && (
                                <div className="mb-4">
                                  <div className="bg-gradient-to-r from-gray-50 to-gray-50/30 rounded-xl p-4 border border-gray-100">
                                    <div className="flex items-center mb-2">
                                      <div className="w-6 h-6 bg-[#FF8C00]/10 rounded-lg flex items-center justify-center mr-2">
                                        <svg className="w-3 h-3 text-[#FF8C00]" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                        </svg>
                                      </div>
                                      <h4 className="text-sm font-semibold text-gray-900">Cover Letter</h4>
                                    </div>
                                    <p className="text-gray-700 text-sm leading-relaxed" style={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden'
                                    }}>
                                      {application.coverLetter}
                                    </p>
                                    {application.coverLetter.length > 100 && (
                                      <button className="text-[#FF8C00] text-xs mt-2 hover:underline font-medium">
                                        Read more
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Action Panel */}
                          <div className="flex flex-col items-end space-y-3 ml-6">
                            {/* Status Selector */}
                            <select
                              value={application.status || 'pending'}
                              onChange={(e) => handleStatusChange(application.id, e.target.value)}
                              className="text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#FF8C00]/20 focus:border-[#FF8C00] bg-white transition-all min-w-[160px]"
                            >
                              <option value="pending">📋 Pending</option>
                              <option value="reviewed">👀 Reviewing</option>
                              <option value="hired">✅ Hired</option>
                              <option value="rejected">❌ Rejected</option>
                            </select>
                            
                            {/* Action Buttons */}
                            <div className="flex items-center space-x-2">
                              <button className="px-4 py-2 bg-blue-500 text-white text-sm rounded-xl hover:bg-blue-600 transition-all hover:scale-105 shadow-sm flex items-center">
                                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                </svg>
                                Contact
                              </button>
                              
                              <button className="px-4 py-2 bg-[#FF8C00] text-white text-sm rounded-xl hover:bg-[#FF8C00]/90 transition-all hover:scale-105 shadow-sm flex items-center">
                                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                </svg>
                                Profile
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Progress Indicator */}
                        <div className="mt-4">
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full transition-all duration-700 ease-out rounded-full ${
                              application.status === 'pending' ? 'w-1/4 bg-gradient-to-r from-yellow-400 to-yellow-500' :
                              application.status === 'reviewed' ? 'w-2/4 bg-gradient-to-r from-blue-400 to-blue-500' :
                              application.status === 'hired' ? 'w-full bg-gradient-to-r from-green-400 to-green-500' :
                              application.status === 'rejected' ? 'w-full bg-gradient-to-r from-red-400 to-red-500' :
                              'w-1/4 bg-gradient-to-r from-yellow-400 to-yellow-500'
                            }`}></div>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Edit Job Modal */}
      <JobModal 
        show={showEditModal} 
        onClose={() => setShowEditModal(false)} 
        jobForm={jobForm}
        setJobForm={setJobForm}
        skillInput={skillInput}
        setSkillInput={setSkillInput}
        benefitInput={benefitInput}
        setBenefitInput={setBenefitInput}
        useBusinessLocation={useBusinessLocation}
        setUseBusinessLocation={setUseBusinessLocation}
        customLocation={customLocation}
        setCustomLocation={setCustomLocation}
        businessProfile={businessProfile}
        memoizedBusinessAddress={memoizedBusinessAddress}
        submitting={submitting}
        onSubmit={handleSubmitJob}
        onAddSkill={addSkill}
        onRemoveSkill={removeSkill}
        onAddBenefit={addBenefit}
        onRemoveBenefit={removeBenefit}
      />
    </div>
  );
};

export default BusinessJobDetail; 